import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ImageUploadButton from './editor/ImageUploadButton';
import TableBuilder from './editor/TableBuilder';

/**
 * NoteToolbar — Editor formatting toolbar.
 *
 * Since we're using a plain contentEditable editor (no Slate/Draft.js dependency),
 * commands operate via document.execCommand for formatting.
 *
 * Also houses the Image Upload button and Table Builder trigger.
 */

const FONT_FAMILIES = [
    { label: 'Arial', value: 'Arial, sans-serif' },
    { label: 'Times New Roman', value: '"Times New Roman", serif' },
    { label: 'Courier New', value: '"Courier New", monospace' },
    { label: 'Georgia', value: 'Georgia, serif' },
    { label: 'Verdana', value: 'Verdana, sans-serif' },
    { label: 'Trebuchet MS', value: '"Trebuchet MS", sans-serif' },
    { label: 'Comic Sans MS', value: '"Comic Sans MS", cursive' },
    { label: 'Impact', value: 'Impact, fantasy' },
    { label: 'Lucida Console', value: '"Lucida Console", monospace' },
    { label: 'System Font', value: 'system-ui, sans-serif' },
];

const TEXT_COLORS = ['#000000', '#D32F2F', '#2E7D32', '#1565C0', '#EF6C00', '#6A1B9A', '#616161'];
const HIGHLIGHT_COLORS = ['#FFEB3B', '#90EE90', '#ADD8E6', '#FFD700', '#FFB6C1'];

function getSelectionNode() {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return null;
    let node = selection.focusNode;
    if (!node) return null;
    if (node.nodeType === Node.TEXT_NODE) node = node.parentElement;
    return node;
}

function applyInlineStyleToSelection(styleObj) {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0 || selection.isCollapsed) return;
    const range = selection.getRangeAt(0);
    const span = document.createElement('span');
    Object.entries(styleObj).forEach(([key, val]) => {
        if (val !== undefined && val !== null && val !== '') span.style[key] = val;
    });
    span.appendChild(range.extractContents());
    range.insertNode(span);
    selection.removeAllRanges();
    const newRange = document.createRange();
    newRange.selectNodeContents(span);
    selection.addRange(newRange);
}

function readCurrentStyles() {
    const node = getSelectionNode();
    if (!node) return null;
    const style = window.getComputedStyle(node);
    return {
        fontFamily: style.fontFamily,
        fontSize: style.fontSize,
        color: style.color,
        backgroundColor: style.backgroundColor,
        fontWeight: style.fontWeight,
        fontStyle: style.fontStyle,
        textDecoration: style.textDecorationLine,
        verticalAlign: style.verticalAlign,
    };
}

export default function NoteToolbar({ editorRef, companyId, tabId, onContentChange }) {
    const [tableBuilderOpen, setTableBuilderOpen] = useState(false);
    const [fontFamily, setFontFamily] = useState(FONT_FAMILIES[0].value);
    const [fontSize, setFontSize] = useState(14);
    const [textColor, setTextColor] = useState('#000000');
    const [highlightColor, setHighlightColor] = useState('#FFEB3B');
    const [formatPainter, setFormatPainter] = useState({ active: false, sticky: false, styles: null });
    const savedRangeRef = useRef(null);

    const saveSelection = useCallback(() => {
        const selection = window.getSelection();
        if (!selection || selection.rangeCount === 0) return;
        const range = selection.getRangeAt(0);
        if (editorRef?.current && editorRef.current.contains(range.commonAncestorContainer)) {
            savedRangeRef.current = range.cloneRange();
        }
    }, [editorRef]);

    const restoreSelection = useCallback(() => {
        if (!savedRangeRef.current) return;
        const selection = window.getSelection();
        if (!selection) return;
        selection.removeAllRanges();
        selection.addRange(savedRangeRef.current);
    }, []);

    const exec = useCallback((cmd, arg) => {
        editorRef?.current?.focus();
        document.execCommand(cmd, false, arg || null);
        setTimeout(() => onContentChange?.(), 0);
    }, [editorRef, onContentChange]);

    const toolbarButtonClass = useMemo(
        () => `notes-toolbar-btn ${formatPainter.active ? 'notes-toolbar-btn-painter-active' : ''}`,
        [formatPainter.active],
    );

    const applyStyle = useCallback((styleObj) => {
        editorRef?.current?.focus();
        applyInlineStyleToSelection(styleObj);
        onContentChange?.();
    }, [editorRef, onContentChange]);

    const applyFontFamily = useCallback((nextFont) => {
        setFontFamily(nextFont);
        applyStyle({ fontFamily: nextFont });
    }, [applyStyle]);

    const applyFontSize = useCallback((nextSize) => {
        const parsed = Number(nextSize);
        if (!parsed || parsed < 8 || parsed > 96) return;
        setFontSize(parsed);
        applyStyle({ fontSize: `${parsed}px` });
    }, [applyStyle]);

    const applyTextColor = useCallback((nextColor) => {
        setTextColor(nextColor);
        applyStyle({ color: nextColor });
    }, [applyStyle]);

    const applyHighlight = useCallback((nextColor) => {
        setHighlightColor(nextColor);
        applyStyle({ backgroundColor: nextColor });
    }, [applyStyle]);

    const clearFormatting = useCallback(() => {
        exec('removeFormat');
        exec('unlink');
    }, [exec]);

    const startFormatPainter = useCallback((sticky = false) => {
        const styles = readCurrentStyles();
        if (!styles) return;
        const mapped = {
            fontFamily: styles.fontFamily,
            fontSize: styles.fontSize,
            color: styles.color,
            backgroundColor: styles.backgroundColor === 'rgba(0, 0, 0, 0)' ? '' : styles.backgroundColor,
            fontWeight: Number(styles.fontWeight) >= 600 ? '700' : '',
            fontStyle: styles.fontStyle === 'italic' ? 'italic' : '',
            textDecoration: styles.textDecoration.includes('underline') ? 'underline' : (styles.textDecoration.includes('line-through') ? 'line-through' : ''),
            verticalAlign: styles.verticalAlign === 'sub' || styles.verticalAlign === 'super' ? styles.verticalAlign : '',
        };
        setFormatPainter({ active: true, sticky, styles: mapped });
    }, []);

    useEffect(() => {
        if (!formatPainter.active || !editorRef?.current) return;
        const editor = editorRef.current;
        const applyPainter = () => {
            const selection = window.getSelection();
            if (!selection || selection.isCollapsed || !formatPainter.styles) return;
            applyInlineStyleToSelection(formatPainter.styles);
            onContentChange?.();
            if (!formatPainter.sticky) {
                setFormatPainter({ active: false, sticky: false, styles: null });
            }
        };
        editor.classList.add('notes-editor-painter-mode');
        editor.addEventListener('mouseup', applyPainter);
        return () => {
            editor.classList.remove('notes-editor-painter-mode');
            editor.removeEventListener('mouseup', applyPainter);
        };
    }, [editorRef, formatPainter, onContentChange]);

    const handleTableInsert = useCallback((html) => {
        editorRef?.current?.focus();
        restoreSelection();
        document.execCommand('insertHTML', false, html);
        onContentChange?.();
        savedRangeRef.current = null;
    }, [editorRef, onContentChange, restoreSelection]);

    return (
        <div className="notes-toolbar" role="toolbar" aria-label="Text formatting">
            <div className="notes-toolbar-row">
                <button type="button" className="notes-toolbar-btn" title="Undo (Ctrl+Z)" onMouseDown={(e) => { e.preventDefault(); exec('undo'); }}>↶</button>
                <button type="button" className="notes-toolbar-btn" title="Redo (Ctrl+Y)" onMouseDown={(e) => { e.preventDefault(); exec('redo'); }}>↷</button>
                <div className="notes-toolbar-sep" />

                <select className="notes-toolbar-select" value={fontFamily} onChange={(e) => applyFontFamily(e.target.value)} title="Font family">
                    {FONT_FAMILIES.map((f) => <option key={f.label} value={f.value} style={{ fontFamily: f.value }}>{f.label}</option>)}
                </select>

                <input
                    className="notes-toolbar-size"
                    type="number"
                    min="8"
                    max="96"
                    value={fontSize}
                    onChange={(e) => setFontSize(Number(e.target.value) || 14)}
                    onBlur={() => applyFontSize(fontSize)}
                    onKeyDown={(e) => { if (e.key === 'Enter') applyFontSize(fontSize); }}
                    title="Font size"
                />

                <button type="button" className="notes-toolbar-btn" title="Bold (Ctrl+B)" style={{ fontWeight: 700 }} onMouseDown={(e) => { e.preventDefault(); exec('bold'); }}>B</button>
                <button type="button" className="notes-toolbar-btn" title="Italic (Ctrl+I)" style={{ fontStyle: 'italic' }} onMouseDown={(e) => { e.preventDefault(); exec('italic'); }}>I</button>
                <button type="button" className="notes-toolbar-btn" title="Underline (Ctrl+U)" style={{ textDecoration: 'underline' }} onMouseDown={(e) => { e.preventDefault(); exec('underline'); }}>U</button>
                <button type="button" className="notes-toolbar-btn" title="Strikethrough" style={{ textDecoration: 'line-through' }} onMouseDown={(e) => { e.preventDefault(); exec('strikeThrough'); }}>S</button>

                <label className="notes-toolbar-color-wrap" title="Text color">
                    <span className="notes-toolbar-color-label" style={{ borderBottomColor: textColor }}>A</span>
                    <input type="color" className="notes-toolbar-color" value={textColor} onChange={(e) => applyTextColor(e.target.value)} />
                </label>
                <label className="notes-toolbar-color-wrap" title="Highlight color">
                    <span className="notes-toolbar-color-label" style={{ background: highlightColor }}>🖍️</span>
                    <input type="color" className="notes-toolbar-color" value={highlightColor} onChange={(e) => applyHighlight(e.target.value)} />
                </label>

                <button type="button" className="notes-toolbar-btn" title="Subscript (Ctrl+=)" onMouseDown={(e) => { e.preventDefault(); exec('subscript'); }}>X₂</button>
                <button type="button" className="notes-toolbar-btn" title="Superscript (Ctrl+Shift+=)" onMouseDown={(e) => { e.preventDefault(); exec('superscript'); }}>X²</button>

                <button
                    type="button"
                    className={toolbarButtonClass}
                    title="Format painter (double-click for sticky mode)"
                    onClick={(e) => {
                        e.preventDefault();
                        if (formatPainter.active) {
                            setFormatPainter({ active: false, sticky: false, styles: null });
                            return;
                        }
                        startFormatPainter(false);
                    }}
                    onDoubleClick={(e) => {
                        e.preventDefault();
                        startFormatPainter(true);
                    }}
                >
                    🖌️
                </button>
                <button type="button" className="notes-toolbar-btn" title="Clear formatting (Ctrl+\\)" onMouseDown={(e) => { e.preventDefault(); clearFormatting(); }}>Tx</button>
            </div>

            <div className="notes-toolbar-row">
                <button type="button" className="notes-toolbar-btn" title="Heading 1" onMouseDown={(e) => { e.preventDefault(); exec('formatBlock', 'H2'); }}>H1</button>
                <button type="button" className="notes-toolbar-btn" title="Heading 2" onMouseDown={(e) => { e.preventDefault(); exec('formatBlock', 'H3'); }}>H2</button>
                <div className="notes-toolbar-sep" />

                <button type="button" className="notes-toolbar-btn" title="Bullet list" onMouseDown={(e) => { e.preventDefault(); exec('insertUnorderedList'); }}>•</button>
                <button type="button" className="notes-toolbar-btn" title="Numbered list" onMouseDown={(e) => { e.preventDefault(); exec('insertOrderedList'); }}>1.</button>
                <div className="notes-toolbar-sep" />

                <button type="button" className="notes-toolbar-btn" title="Align left (Ctrl+L)" onMouseDown={(e) => { e.preventDefault(); exec('justifyLeft'); }}>≡</button>
                <button type="button" className="notes-toolbar-btn" title="Align center (Ctrl+E)" onMouseDown={(e) => { e.preventDefault(); exec('justifyCenter'); }}>≣</button>
                <button type="button" className="notes-toolbar-btn" title="Align right (Ctrl+R)" onMouseDown={(e) => { e.preventDefault(); exec('justifyRight'); }}>≡</button>
                <button type="button" className="notes-toolbar-btn" title="Justify (Ctrl+J)" onMouseDown={(e) => { e.preventDefault(); exec('justifyFull'); }}>☰</button>
                <div className="notes-toolbar-sep" />

                <button type="button" className="notes-toolbar-btn" title="Indent (Tab)" onMouseDown={(e) => { e.preventDefault(); exec('indent'); }}>→</button>
                <button type="button" className="notes-toolbar-btn" title="Outdent (Shift+Tab)" onMouseDown={(e) => { e.preventDefault(); exec('outdent'); }}>←</button>

                <button type="button" className="notes-toolbar-btn" title="Block quote" onMouseDown={(e) => { e.preventDefault(); exec('formatBlock', 'BLOCKQUOTE'); }}>❝</button>
                <button type="button" className="notes-toolbar-btn" title="Divider" onMouseDown={(e) => { e.preventDefault(); exec('insertHorizontalRule'); }}>―</button>
            </div>

            <div className="notes-toolbar-row">
                <ImageUploadButton
                    companyId={companyId}
                    tabId={tabId}
                    editorRef={editorRef}
                    onContentChange={onContentChange}
                />
                <button
                    type="button"
                    className="notes-toolbar-btn"
                    title="Insert table"
                    onMouseDown={(e) => {
                        e.preventDefault();
                        saveSelection();
                        setTableBuilderOpen(true);
                    }}
                >
                    📊
                </button>
                <TableBuilder
                    open={tableBuilderOpen}
                    onClose={() => setTableBuilderOpen(false)}
                    onInsert={handleTableInsert}
                />

                <div className="notes-toolbar-presets" aria-hidden>
                    {TEXT_COLORS.map((color) => (
                        <button key={color} type="button" className="notes-toolbar-swatch" style={{ background: color }} onMouseDown={(e) => { e.preventDefault(); applyTextColor(color); }} />
                    ))}
                    {HIGHLIGHT_COLORS.map((color) => (
                        <button key={`hl-${color}`} type="button" className="notes-toolbar-swatch notes-toolbar-swatch-hl" style={{ background: color }} onMouseDown={(e) => { e.preventDefault(); applyHighlight(color); }} />
                    ))}
                </div>
            </div>
        </div>
    );
}
